
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Secure Immunization Service
 * Uses backend API endpoints instead of direct Firestore access
 */

// Subscribe to immunizations for a patient
export function subscribeToImmunizations(patientId, onUpdate) {
  // For now, we'll use polling as a fallback
  // In production, this should use WebSocket or Server-Sent Events
  const interval = setInterval(async () => {
    try {
      const immunizations = await getPatientImmunizations(patientId);
      onUpdate(immunizations);
    } catch (error) {
      console.error('Error fetching immunizations:', error);
      onUpdate([]);
    }
  }, 5000); // Poll every 5 seconds

  // Return cleanup function
  return () => clearInterval(interval);
}

// Get immunizations for a specific patient
export async function getPatientImmunizations(patientId) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE}/clinical/immunizations`, {
      params: { patientId },
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 200) {
      return response.data;
    }

    throw new Error('Failed to get patient immunizations');
  } catch (error) {
    console.error('Error getting patient immunizations:', error);
    throw error;
  }
}

// Create a new immunization
export async function createImmunization(immunizationData) {
  try {
    const token = await getAuthToken();
    const response = await axios.post(`${API_BASE}/clinical/immunizations`, immunizationData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 201) {
      return response.data;
    }

    throw new Error('Failed to create immunization');
  } catch (error) {
    console.error('Error creating immunization:', error);
    throw error;
  }
}

// Update an existing immunization
export async function updateImmunization(immunizationId, updateData) {
  try {
    const token = await getAuthToken();
    const response = await axios.put(`${API_BASE}/clinical/immunizations/${immunizationId}`, updateData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 200) {
      return response.data;
    }

    throw new Error('Failed to update immunization');
  } catch (error) {
    console.error('Error updating immunization:', error);
    throw error;
  }
}

// Get immunization by ID
export async function getImmunizationById(immunizationId) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE}/clinical/immunizations/${immunizationId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 200) {
      return response.data;
    }

    throw new Error('Failed to get immunization');
  } catch (error) {
    console.error('Error getting immunization:', error);
    throw error;
  }
}

// Delete an immunization
export async function deleteImmunization(immunizationId) {
  try {
    const token = await getAuthToken();
    const response = await axios.delete(`${API_BASE}/clinical/immunizations/${immunizationId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 200) {
      return response.data;
    }

    throw new Error('Failed to delete immunization');
  } catch (error) {
    console.error('Error deleting immunization:', error);
    throw error;
  }
}

// Get immunization history for a patient
export async function getImmunizationHistory(patientId, options = {}) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE}/clinical/immunizations/history`, {
      params: { patientId, ...options },
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 200) {
      return response.data;
    }

    throw new Error('Failed to get immunization history');
  } catch (error) {
    console.error('Error getting immunization history:', error);
    throw error;
  }
}
